/**
 * Parse / validate managed query `ddx-temporal-extraction` output (MC-9: never coerce resolved → absent).
 */

import {
  TEMPORAL_BUCKETS,
  BIOLOGICAL_SEX,
  SMOKING_STATUS,
  ALCOHOL_USE,
  DRUG_USE,
  HOUSING_STATUS,
  OCCUPATION_RISK,
  IMMUNOSUPPRESSION_STATUS,
  PREGNANCY_STATUS,
  ANTICOAGULATION_STATUS,
  OBESITY_STATUS,
  PRIOR_EPISODE_STATUS,
  TRAVEL_HISTORY,
  MECHANISM_OF_INJURY,
  RELEVANT_COMORBIDITIES,
  FAMILY_HISTORY_FLAGS,
  VACCINATION_GAPS,
  HIGH_IMPACT_MEDICATIONS,
  SEXUAL_HISTORY_FLAGS,
  normalizeTemporalBucket,
  normalizeOnsetPattern,
  normalizeFeatureStatus,
  normalizeFeatureType,
  normalizeAgeBand,
  normalizeBiologicalSex,
  normalizeSmokingStatus,
  normalizeAlcoholUse,
  normalizeDrugUseItem,
  normalizeHousingStatus,
  normalizeOccupationRisk,
  normalizeImmunosuppressionStatus,
  normalizePregnancyStatus,
  normalizeAnticoagulationStatus,
  normalizeObesityStatus,
  normalizeFamilyHistoryFlagItem,
  normalizePriorEpisodeStatus,
  normalizeRelevantComorbidityItem,
  normalizeVaccinationGapItem,
  normalizeTravelHistory,
  normalizeSexualHistoryFlagItem,
  normalizeHighImpactMedicationItem,
  normalizeMechanismOfInjury,
} from './ddxTemporalTaxonomy.js';

/**
 * @returns {import('./ddxTypes.js').PatientContextProfile}
 */
export function createEmptyPatientContext() {
  return {
    ageBand: null,
    ageSpecific: null,
    biologicalSex: BIOLOGICAL_SEX.UNKNOWN,
    smokingStatus: SMOKING_STATUS.UNKNOWN,
    packYears: null,
    alcoholUse: ALCOHOL_USE.UNKNOWN,
    drugUse: [],
    housingStatus: HOUSING_STATUS.UNKNOWN,
    occupationRisk: OCCUPATION_RISK.UNKNOWN,
    sexualHistoryFlags: [],
    immunosuppressionStatus: IMMUNOSUPPRESSION_STATUS.UNKNOWN,
    pregnancyStatus: PREGNANCY_STATUS.UNKNOWN,
    anticoagulationStatus: ANTICOAGULATION_STATUS.UNKNOWN,
    obesityStatus: OBESITY_STATUS.NORMAL_OR_UNKNOWN,
    relevantComorbidities: [],
    familyHistoryFlags: [],
    highImpactMedications: [],
    vaccinationGaps: [],
    priorEpisodeStatus: PRIOR_EPISODE_STATUS.UNKNOWN,
    travelHistory: TRAVEL_HISTORY.UNKNOWN,
    mechanismOfInjury: MECHANISM_OF_INJURY.UNKNOWN,
    confidences: [],
    flaggedHighImpactFactors: [],
    activeContextWarnings: [],
  };
}

/**
 * @param {unknown} raw
 * @param {import('./ddxTypes.js').TemporalExtractionDemographics} demographics
 * @returns {import('./ddxTypes.js').PatientContextProfile}
 */
export function parsePatientContext(raw, demographics) {
  const base = createEmptyPatientContext();
  const pc = raw && typeof raw === 'object' ? raw : {};

  const ageBand = normalizeAgeBand(pc.ageBand != null ? pc.ageBand : demographics?.ageBand);
  base.ageBand = ageBand;
  base.ageSpecific =
    typeof pc.ageSpecific === 'number' && !Number.isNaN(pc.ageSpecific) ? pc.ageSpecific : demographics?.ageSpecific ?? null;

  base.biologicalSex = normalizeBiologicalSex(pc.biologicalSex != null ? pc.biologicalSex : demographics?.sex);
  base.smokingStatus = normalizeSmokingStatus(pc.smokingStatus);
  base.packYears =
    typeof pc.packYears === 'number' && !Number.isNaN(pc.packYears) ? pc.packYears : null;
  base.alcoholUse = normalizeAlcoholUse(pc.alcoholUse);

  base.drugUse = normalizeMultiEnum(pc.drugUse, normalizeDrugUseItem, DRUG_USE.UNKNOWN, [DRUG_USE.NONE]);
  base.housingStatus = normalizeHousingStatus(pc.housingStatus);
  base.occupationRisk = normalizeOccupationRisk(pc.occupationRisk);
  base.sexualHistoryFlags = normalizeMultiEnum(
    pc.sexualHistoryFlags,
    normalizeSexualHistoryFlagItem,
    SEXUAL_HISTORY_FLAGS.UNKNOWN,
    []
  );

  base.immunosuppressionStatus = normalizeImmunosuppressionStatus(pc.immunosuppressionStatus);
  base.pregnancyStatus = normalizePregnancyStatus(pc.pregnancyStatus);

  base.anticoagulationStatus = normalizeAnticoagulationStatus(pc.anticoagulationStatus);
  base.obesityStatus = normalizeObesityStatus(pc.obesityStatus);
  base.relevantComorbidities = normalizeMultiEnum(
    pc.relevantComorbidities,
    normalizeRelevantComorbidityItem,
    RELEVANT_COMORBIDITIES.UNKNOWN,
    [RELEVANT_COMORBIDITIES.NONE_KNOWN]
  );
  base.familyHistoryFlags = normalizeMultiEnum(
    pc.familyHistoryFlags,
    normalizeFamilyHistoryFlagItem,
    FAMILY_HISTORY_FLAGS.UNKNOWN,
    [FAMILY_HISTORY_FLAGS.NONE_KNOWN]
  );
  base.highImpactMedications = normalizeMultiEnum(
    pc.highImpactMedications,
    normalizeHighImpactMedicationItem,
    HIGH_IMPACT_MEDICATIONS.UNKNOWN,
    [HIGH_IMPACT_MEDICATIONS.NONE_KNOWN]
  );
  base.vaccinationGaps = normalizeMultiEnum(
    pc.vaccinationGaps,
    normalizeVaccinationGapItem,
    VACCINATION_GAPS.UNKNOWN,
    [VACCINATION_GAPS.NONE_KNOWN]
  );
  base.priorEpisodeStatus = normalizePriorEpisodeStatus(pc.priorEpisodeStatus);
  base.travelHistory = normalizeTravelHistory(pc.travelHistory);
  base.mechanismOfInjury = normalizeMechanismOfInjury(pc.mechanismOfInjury);

  const conf = Array.isArray(pc.confidences) ? pc.confidences : [];
  base.confidences = conf
    .filter((c) => c && typeof c === 'object')
    .map((c) => ({
      factor: c.factor != null ? String(c.factor).trim() : '',
      confidence: typeof c.confidence === 'number' && !Number.isNaN(c.confidence) ? c.confidence : 0,
      evidence: c.evidence != null ? String(c.evidence).trim() : undefined,
    }))
    .filter((c) => c.factor);

  const fh = Array.isArray(pc.flaggedHighImpactFactors) ? pc.flaggedHighImpactFactors : [];
  base.flaggedHighImpactFactors = [...new Set(fh.map((x) => String(x).trim()).filter(Boolean))];

  const aw = Array.isArray(pc.activeContextWarnings) ? pc.activeContextWarnings : [];
  base.activeContextWarnings = [...new Set(aw.map((x) => String(x).trim()).filter(Boolean))];

  enrichPatientContextFlags(base);
  return base;
}

/**
 * @param {unknown} arr
 * @param {(x: unknown) => string} norm
 * @param {string} unknownToken
 * @param {string[]} neutralTokens - if result only neutral, return []
 */
function normalizeMultiEnum(arr, norm, unknownToken, neutralTokens) {
  if (!Array.isArray(arr)) return [];
  const out = [];
  const seen = new Set();
  for (const x of arr) {
    const n = norm(x);
    if (n === unknownToken) continue;
    if (!seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  }
  if (out.length === 0) return [];
  if (neutralTokens.length && out.length === 1 && neutralTokens.includes(out[0])) return [];
  return out;
}

/**
 * Augment flaggedHighImpactFactors from structured context (spec safety net).
 * @param {import('./ddxTypes.js').PatientContextProfile} pc
 */
function enrichPatientContextFlags(pc) {
  const flagged = new Set(pc.flaggedHighImpactFactors || []);
  const im = pc.immunosuppressionStatus;
  if (im && im !== IMMUNOSUPPRESSION_STATUS.NONE && im !== IMMUNOSUPPRESSION_STATUS.UNKNOWN) {
    flagged.add('immunosuppressionStatus');
  }
  const pr = pc.pregnancyStatus;
  if (
    pr === PREGNANCY_STATUS.PREGNANT_FIRST ||
    pr === PREGNANCY_STATUS.PREGNANT_SECOND ||
    pr === PREGNANCY_STATUS.PREGNANT_THIRD ||
    pr === PREGNANCY_STATUS.POSTPARTUM
  ) {
    flagged.add('pregnancyStatus');
  }
  for (const d of pc.drugUse || []) {
    if (d === DRUG_USE.IVDU || d === DRUG_USE.COCAINE) {
      flagged.add('drugUse');
      break;
    }
  }
  for (const c of pc.relevantComorbidities || []) {
    if (c === RELEVANT_COMORBIDITIES.MALIGNANCY || c === RELEVANT_COMORBIDITIES.PRIOR_PE_DVT) {
      flagged.add('relevantComorbidities');
      break;
    }
  }
  const ac = pc.anticoagulationStatus;
  if (ac && ac !== ANTICOAGULATION_STATUS.NONE && ac !== ANTICOAGULATION_STATUS.UNKNOWN) {
    flagged.add('anticoagulationStatus');
  }
  const th = pc.travelHistory;
  if (th && th !== TRAVEL_HISTORY.NONE && th !== TRAVEL_HISTORY.DOMESTIC && th !== TRAVEL_HISTORY.UNKNOWN) {
    flagged.add('travelHistory');
  }
  const hs = pc.housingStatus;
  if (hs === HOUSING_STATUS.UNHOUSED || hs === HOUSING_STATUS.INCARCERATED) {
    flagged.add('housingStatus');
  }
  pc.flaggedHighImpactFactors = [...flagged];
}

/**
 * @param {string} text
 * @returns {string}
 */
export function stripJsonFences(text) {
  if (text == null || typeof text !== 'string') return '';
  let s = text.trim();
  const fence = /^```(?:json)?\s*([\s\S]*?)```$/im;
  const m = s.match(fence);
  if (m) s = m[1].trim();
  return s;
}

/**
 * @param {unknown} raw - LLM output string or parsed object
 * @returns {import('./ddxTypes.js').TemporalExtractionResult}
 */
export function parseAndValidateExtraction(raw) {
  let obj;
  if (typeof raw === 'string') {
    const s = stripJsonFences(raw);
    try {
      obj = JSON.parse(s);
    } catch {
      obj = {};
    }
  } else if (raw && typeof raw === 'object') {
    obj = raw;
  } else {
    obj = {};
  }

  const acuityRaw = obj.acuityEstimate;
  const acuityEstimate = normalizeTemporalBucket(acuityRaw) || TEMPORAL_BUCKETS.INDETERMINATE;

  const temporalRedFlags = Array.isArray(obj.temporalRedFlags)
    ? obj.temporalRedFlags.map((x) => String(x).trim()).filter(Boolean)
    : [];

  const keyFeatures = [];
  const kf = Array.isArray(obj.keyFeatures) ? obj.keyFeatures : [];
  for (const item of kf) {
    if (!item || typeof item !== 'object') continue;
    const fs = normalizeFeatureStatus(item.featureStatus);
    keyFeatures.push({
      featureId: item.featureId == null || item.featureId === '' ? null : String(item.featureId),
      rawLabel: String(item.rawLabel || '').trim() || 'unknown',
      featureType: normalizeFeatureType(item.featureType) || 'symptom',
      featureStatus: fs,
      onsetPattern: normalizeOnsetPattern(item.onsetPattern),
      durationBucket: item.durationBucket != null ? String(item.durationBucket).trim() : null,
      temporalContext: item.temporalContext != null ? String(item.temporalContext) : '',
      confidence: typeof item.confidence === 'number' && !Number.isNaN(item.confidence) ? item.confidence : undefined,
    });
  }

  const dem = obj.demographics && typeof obj.demographics === 'object' ? obj.demographics : {};
  const demographics = {
    ageBand: normalizeAgeBand(dem.ageBand),
    sex: dem.sex != null ? String(dem.sex).trim().toLowerCase() : null,
    ageSpecific: typeof dem.ageSpecific === 'number' ? dem.ageSpecific : undefined,
  };

  const patientContext = parsePatientContext(obj.patientContext, demographics);
  if (!patientContext.ageBand && demographics.ageBand) {
    patientContext.ageBand = demographics.ageBand;
  }
  if (patientContext.biologicalSex === BIOLOGICAL_SEX.UNKNOWN && demographics.sex) {
    const s = demographics.sex;
    if (s === 'male' || s === 'female') patientContext.biologicalSex = s;
  }

  return {
    acuityEstimate,
    temporalRedFlags,
    keyFeatures,
    demographics,
    patientContext,
  };
}

/**
 * Map durationBucket string from extractor to TEMPORAL_BUCKETS value.
 * @param {string | null | undefined} durationBucket
 * @returns {string | null}
 */
export function mapDurationBucketToTemporal(durationBucket) {
  if (durationBucket == null) return null;
  const s = String(durationBucket).trim().toLowerCase();
  const direct = normalizeTemporalBucket(s);
  if (direct) return direct;
  if (s.includes('hour') || s === 'hours_to_day') return TEMPORAL_BUCKETS.ACUTE;
  if (s.includes('day') || s.includes('week')) return TEMPORAL_BUCKETS.SUBACUTE;
  if (s.includes('month') || s.includes('year') || s.includes('chronic')) return TEMPORAL_BUCKETS.CHRONIC;
  if (s.includes('second') || s.includes('minute')) return TEMPORAL_BUCKETS.HYPERACUTE;
  return TEMPORAL_BUCKETS.INDETERMINATE;
}
